import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ImportFormLoading from '@Components/import-form/import-form-loading';
import ImportSuccessfulComponent from '@Components/import-form/import-successful-component';
import { Button } from '@app/shadcn/components/ui/button';
import { useToast } from '@app/shadcn/components/ui/use-toast';
import useDrivePicker from '@fyelci/react-google-drive-picker';

import GoogleFormsImportNotice from '@app/components/form-integrations/google-forms-import-notice';
import ImportErrorView from '@app/components/form-integrations/import-error-view';
import { useModal } from '@app/components/modal-views/context';
import FullScreenLoader from '@app/components/ui/fullscreen-loader';
import { buttonConstant } from '@app/constants/locales/button';
import { googleFormsRefusal, useGoogleFormsImportMode } from '@app/lib/hooks/use-google-forms-import-mode';
import { StandardFormDto } from '@app/models/dtos/form';
import { initFormState } from '@app/store/forms/slice';

import { useIsMobile } from '@app/lib/hooks/use-breakpoint';
import { selectAuth } from '@app/store/auth/slice';
import { useAppSelector } from '@app/store/hooks';
import { useImportFormMutation, useLazyGetSingleFormFromProviderQuery, useVerifyFormTokenMutation } from '@app/store/redux/import-api';
import { selectWorkspace } from '@app/store/workspaces/slice';
import { fireworks } from '@app/utils/confetti';
import { getEditFormURL } from '@app/utils/url-utils';
import { useRouter } from 'next/navigation';

export default function ImportFormModal() {
    const { toast } = useToast();
    const router = useRouter();
    const { closeModal } = useModal();

    const workspace = useAppSelector(selectWorkspace);

    const { t } = useTranslation();
    const [verifyToken, { isLoading, data, error }] = useVerifyFormTokenMutation();
    // GOOGLE_FORMS_IMPORT: nothing is asked of Google unless imports are on;
    // a refusal from the backend (403 readonly, 410 off) says the same.
    const { mode, isLoading: modeLoading } = useGoogleFormsImportMode();
    const [refused, setRefused] = useState<'readonly' | 'off' | null>(null);
    const blocked = refused ?? googleFormsRefusal(error) ?? (mode === 'readonly' || mode === 'off' ? mode : null);

    const [openPicker] = useDrivePicker();

    const [form, setForm] = useState<StandardFormDto>(initFormState);
    const [formTitle, setFormTitle] = useState('');
    const isMobile = useIsMobile();
    const auth = useAppSelector(selectAuth);

    const [importForm, importFormResult] = useImportFormMutation();
    const [singleFormFromProviderTrigger, singleFormFromProviderResult] = useLazyGetSingleFormFromProviderQuery();

    useEffect(() => {
        if (modeLoading || mode === 'readonly' || mode === 'off') return;
        verifyToken({ provider: 'google' });
    }, [modeLoading, mode]);

    const handleImportForm = async (formId: string) => {
        const singleForm: any = await singleFormFromProviderTrigger({ provider: 'google', formId: formId });
        const form: any = { ...singleForm?.data, provider: 'google' };
        delete form['clientFormItems'];
        if (singleForm.error) {
            const refusal = googleFormsRefusal(singleForm.error);
            if (refusal) {
                setFormTitle('');
                setRefused(refusal);
                return;
            }
            toast({ description: t('STAFF.IMPORT.FETCH_ERROR'), variant: 'destructive' });
            return;
            closeModal();
        }
        const response: any = await importForm({
            body: { form, response_data_owner: '' },
            provider: 'google',
            workspaceId: workspace.id
        });
        if (response.error && googleFormsRefusal(response.error)) {
            setFormTitle('');
            setRefused(googleFormsRefusal(response.error));
            return;
        }
        if (response.error) {
            toast({ description: t('STAFF.IMPORT.WRONG'), variant: 'destructive' });
            closeModal();
        }
        if (response.data) {
            toast({ description: t('STAFF.IMPORT.IMPORTED_TOAST') });
            setForm(response.data);
            fireworks();
        }
    };

    const customViews: any = [];

    // @ts-ignore
    if (window.google && window.google?.picker) {
        // @ts-ignore
        const customView = new window.google.picker.DocsView(google.picker.ViewId.FORMS);
        // @ts-ignore
        customView?.setMode(window.google.picker.DocsViewMode.LIST);

        customViews.push(customView);
    }
    const openGoogleFilePicker = () => {
        openPicker({
            // @ts-ignore
            clientId: window.PUBLIC_CONFIG?.GOOGLE_CLIENT_ID,
            // @ts-ignore
            developerKey: window.PUBLIC_CONFIG?.GOOGLE_PICKER_API_KEY,
            // viewId: 'FORMS',
            token: data,
            disableDefaultView: true,
            customViews: customViews,
            customScopes: ['https://www.googleapis.com/auth/drive.file'],
            callbackFunction: (data) => {
                if (data.action === 'picked' && data.docs && Array.isArray(data.docs) && data.docs.length > 0) {
                    const formId = data.docs[0].id;
                    const formTitle = data.docs[0].name;
                    setFormTitle(formTitle);
                    handleImportForm(formId);
                } else if (data.action === 'cancel') {
                    closeModal();
                }
            }
        });
    };

    useEffect(() => {
        if (data) {
            openGoogleFilePicker();
        }
    }, [data]);

    useEffect(() => {
        if (form?.formId) {
            isMobile ? router.push(`/${workspace.workspaceName}/dashboard/forms/${form.formId}?view=Preview`) : router.push(getEditFormURL(workspace, form));
        }
    }, [form]);

    if (blocked) {
        return (
            <div className="flex w-full flex-col gap-4 rounded-md bg-white p-6 md:max-w-[480px]">
                <GoogleFormsImportNotice mode={blocked} />
                <Button variant="v2Button" size="sm" className="self-end" onClick={() => closeModal()}>
                    {t(buttonConstant.close)}
                </Button>
            </div>
        );
    }

    if (isLoading || modeLoading) return <FullScreenLoader />;

    if (error) return <ImportErrorView provider={'google'} unauthorizedScopes={(error as any)?.data?.unauthorizedScopes} />;

    return (
        <div className="start relative flex   w-full  flex-col items-center rounded-md bg-white">
            {formTitle && !form?.formId && <ImportFormLoading loadingText={singleFormFromProviderResult.isLoading ? 'Fetching Form' : 'Importing'} formTitle={formTitle} />}
            {!form?.formId && !formTitle && (
                <Button
                    data-umami-event={'Imported Google Form'}
                    variant="primary"
                    size="lg"
                    onClick={() => {
                        openGoogleFilePicker();
                    }}
                >
                    {t('STAFF.IMPORT.OPEN_PICKER')}
                </Button>
            )}
            {form?.formId && <ImportSuccessfulComponent form={form} />}
        </div>
    );
}
